import React, { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import StatTableView from './StatTableView'; 
import StatChartView from './StatChartView'; 

const COLORS = ['#FF7A00', '#0085FF', '#00C22B', '#70E000', '#FF3B30', '#FF9500', '#AF52DE', '#5AC8FA', '#8E8E93'];

function TechActionStatChartBox({ 
  statEvents = [], 
  selectedAction = '전체', 
  setSelectedAction,
  baseWorkMinutes = 0, 
  userCount = 7,
  filterLabels = { type: '', dptInfo: null, list: [] } 
}) {

  const { tableData, dynamicChartData, targetTitleText } = useMemo(() => {
    const baseWorkDaysPerPerson = baseWorkMinutes > 0 ? Number((baseWorkMinutes / 480).toFixed(1)) : 21.0; 

    const members = filterLabels.list && filterLabels.list.length > 0
      ? filterLabels.list.map(item => ({ id: item.id, name: item.nm }))
      : [{ id: 'default', name: '대상자' }];

    let targetTitleText = '부문별 가동률 및 실가동률 정산 현황';
    if (filterLabels.type === 'DPT_LIST') {
      targetTitleText = '전체 부서별 가동률 및 실가동률 정산 현황';
    } else if (filterLabels.type === 'USR_LIST') {
      const dptName = filterLabels.dptInfo?.nm || '해당 부서';
      targetTitleText = `[${dptName}] 담당자별 가동률 및 실가동률 정산 현황`;
    } else if (filterLabels.type === 'SINGLE_USR') {
      const usrName = filterLabels.list?.[0]?.nm || '담당자';
      targetTitleText = `[${usrName}] 가동률 및 실가동률 정산 현황`;
    }

    // ── 🔒 [요구사항 반영] 행(Row) 구분 마스터 가이드 고정 선언 ──
    // 백엔드 데이터 유무와 상관없이 화면 왼쪽의 "구분" 행들은 아래 순서와 이름 그대로 고정출력됩니다.
    const MASTER_INCLUDED = [
      { code: 'PROJECT', name: '프로젝트' },
      { code: 'Maintenance', name: '정기점검' },
      { code: 'TECHSUPPORT', name: '기술지원' },
      { code: 'TECHSUPPORT_H', name: '기술지원(휴일)' },
      { code: 'SALESSUPPORT', name: '영업지원' },
      { code: 'TRAINING', name: '교육수강' },
      { code: 'SELFSTUDY', name: '자체학습' }
    ];

    const MASTER_EXCLUDED = [
      { code: 'PAPERWORK', name: '문서업무' },
      { code: 'VACATION', name: '휴가' }
    ];

    // 마스터 뼈대를 기준으로 배열을 미리 만들고 값을 매핑하는 함수
    const buildFixedRows = (masterList) => {
      return masterList.map(master => {
        const rowData = {
          name: master.name,
          code: master.code,
          memberValues: Array(members.length).fill(0), // 컬럼 크기만큼 0으로 채움
          totalDays: 0
        };

        // 현재 마스터 행 코드와 일치하는 백엔드 이벤트를 찾아 셀마다 값 매핑
        statEvents.forEach(item => {
          const actCode = item?.actionCode || item?.ACTION_CODE || '';
          const targetDptUuid = item?.dptUuid || item?.DPT_UUID || '';
          const targetUsrUuid = item?.usrUuid || item?.USR_UUID || '';
          const days = Number(item?.totalDays || item?.TOTAL_DAYS || 0);

          if (actCode === master.code) {
            const mIdx = members.findIndex(m => {
              if (filterLabels.type === 'DPT_LIST') return m.id === targetDptUuid;
              return m.id === targetUsrUuid;
            });

            if (mIdx !== -1) {
              rowData.memberValues[mIdx] += days;
            } else if (members.length === 1 && members[0].id === 'default') {
              rowData.memberValues[0] += days;
            }
            rowData.totalDays += days;
          }
        });

        return rowData;
      });
    };

    const includedRows = buildFixedRows(MASTER_INCLUDED);
    const excludedRows = buildFixedRows(MASTER_EXCLUDED);
    // ───────────────────────────────────────────────────────────────────────────

    const totalIncludedDays = includedRows.reduce((sum, r) => sum + r.totalDays, 0);
    const includedMemberTotals = members.map((_, mIdx) => 
      Number(includedRows.reduce((sum, r) => sum + r.memberValues[mIdx], 0).toFixed(1))
    );

    const totalExcludedDays = excludedRows.reduce((sum, r) => sum + r.totalDays, 0);
    const excludedMemberTotals = members.map((_, mIdx) => 
      Number(excludedRows.reduce((sum, r) => sum + r.memberValues[mIdx], 0).toFixed(1))
    );

    const totalSumDays = totalIncludedDays + totalExcludedDays;
    const sumMemberTotals = members.map((_, mIdx) => 
      Number((includedMemberTotals[mIdx] + excludedMemberTotals[mIdx]).toFixed(1))
    );

    const calcRate = (days) => {
      const teamTotalBase = baseWorkDaysPerPerson * members.length;
      if (teamTotalBase <= 0) return '0%';
      return `${Math.round((days / teamTotalBase) * 100)}%`;
    };

    const vacationItem = excludedRows.find(r => r.code === 'VACATION' || r.name.includes('휴가'));
    const vacationDays = vacationItem ? vacationItem.totalDays : 0;
    const teamTotalBase = baseWorkDaysPerPerson * members.length;
    const realDenominator = teamTotalBase - vacationDays;
    
    let realWorkingRate = '0%';
    if (realDenominator > 0) {
      realWorkingRate = `${Math.round((totalIncludedDays / realDenominator) * 100)}%`;
    }

    const memberRealRates = members.map((_, mIdx) => {
      // 해당 멤버의 기준 공수 (기본값)
      const memberBase = baseWorkDaysPerPerson;

      // 해당 멤버의 휴가 일수 추출 (excludedRows의 vacation 행의 memberValues[mIdx] 사용)
      const mVacation = excludedRows.find(r => r.code === 'VACATION' || r.name.includes('휴가'))?.memberValues[mIdx] || 0;
      // 해당 멤버의 가동률 포함 소계 (includedMemberTotals[mIdx])
      const mIncluded = includedMemberTotals[mIdx];
      
      const mDenominator = memberBase - mVacation;
      if (mDenominator <= 0) return '0%';
      return `${Math.round((mIncluded / mDenominator) * 100)}%`;
    });

    const chartMap = new Map();
    statEvents.forEach((item) => {
      const days = Number(item?.totalDays || item?.TOTAL_DAYS || 0);
      const actName = item?.actionName || item?.ACTION_NAME || '';
      if (days > 0) {
        chartMap.set(actName, (chartMap.get(actName) || 0) + days);
      }
    });

    const chartList = Array.from(chartMap.entries()).map(([name, value], colorIdx) => {
      const isHighlighted = selectedAction === '전체' || selectedAction === name;
      return {
        name,
        value,
        displayValue: calcRate(value),
        color: isHighlighted ? COLORS[colorIdx % COLORS.length] : '#E5E7EB'
      };
    });

    return {
      tableData: {
        teamMembers: members, 
        tableRows: includedRows,
        excludedRows,
        totalIncludedDays,
        includedMemberTotals,
        totalExcludedDays,
        excludedMemberTotals,
        totalSumDays,
        sumMemberTotals,
        realWorkingRate,
        memberRealRates,
        calcRate
      },
      dynamicChartData: chartList,
      targetTitleText
    };
  }, [statEvents, baseWorkMinutes, selectedAction, filterLabels]); 

  const renderTooltipContent = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload; 
      if (!data) return null;
      return (
        <Box sx={{ backgroundColor: '#fff', p: 1.5, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '13px' }}>
          <Typography variant="body2" sx={{ fontWeight: '600', mb: 0.5, color: '#111827' }}>
            {data.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">가동 비중:</Typography>
            <Typography variant="caption" sx={{ fontWeight: '600' }}>
              {data.displayValue}
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  const includedRowSpan = tableData.tableRows.length > 0 ? tableData.tableRows.length : 1;
  const excludedRowSpan = tableData.excludedRows.length > 0 ? tableData.excludedRows.length : 1;

  const countUnit = filterLabels.type === 'DPT_LIST' ? '개 부서' : '명';
  const displayCount = filterLabels.list && filterLabels.list.length > 0 ? filterLabels.list.length : userCount;

  return (
    <Box sx={{ p: 3, backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', border: '1px solid rgba(0, 0, 0, 0.03)' }}>
      <Typography variant="h6" sx={{ fontWeight: '700', color: '#111827', mb: 2.5 }}>
        {targetTitleText} ({displayCount}{countUnit} 기준)
      </Typography>

      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={8.2}>
          <StatTableView 
            tableData={tableData}
            selectedAction={selectedAction}
            setSelectedAction={setSelectedAction}
            includedRowSpan={includedRowSpan}
            excludedRowSpan={excludedRowSpan}
          />
        </Grid>
        <Grid item xs={12} md={3.8}>
          <StatChartView 
            dynamicChartData={dynamicChartData}
            realWorkingRate={tableData.realWorkingRate}
            renderTooltipContent={renderTooltipContent}
            filterLabels={filterLabels} 
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default TechActionStatChartBox;
